
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Share2, Download, User, Mail, Phone, Link2Off } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { renderHtmlContent, stripHtmlTags } from "@/utils/htmlUtils";
import { LETTER_SHARE_ISSUE, LETTER_SHARE_REVOKE, clipboardShareText, shareTokenFromRpc } from "@/utils/letterShare";
import { generateLetterPDF } from "@/utils/pdfGenerator";

export default function ViewLetter() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: letter, isLoading } = useQuery({
    queryKey: ["letter", id],
    queryFn: async () => {
      if (!id) throw new Error("ID da carta não fornecido");

      console.log("Buscando carta para visualização com ID:", id);

      const { data, error } = await supabase
        .from("letters_with_owner")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      console.log("Dados da carta encontrada:", data);

      if (error) {
        console.error("Erro ao buscar carta:", error);
        throw error;
      }
      if (!data) throw new Error("Carta não encontrada");

      return data;
    },
    enabled: !!id,
  });

  const handleShare = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase.rpc(LETTER_SHARE_ISSUE, { p_letter_id: id });
      const token = shareTokenFromRpc(id, { data, error });
      const shareText = token ? clipboardShareText(window.location.origin, id, token) : null;
      if (!shareText) {
        toast({
          variant: "destructive",
          title: "Link indisponível",
          description: "Não foi possível gerar o link de compartilhamento.",
        });
        return;
      }

      await navigator.clipboard.writeText(shareText);

      toast({
        title: "Link copiado!",
        description: "O link da carta foi copiado para a área de transferência.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Erro ao compartilhar",
        description: "Não foi possível copiar o link da carta.",
      });
    }
  };

  const handleRevokeShare = async () => {
    if (!id) return;

    try {
      const { error } = await supabase.rpc(LETTER_SHARE_REVOKE, { p_letter_id: id });
      if (error) {
        toast({
          variant: "destructive",
          title: "Não foi possível revogar",
          description: "O link continua como estava. Tente novamente.",
        });
        return;
      }

      toast({
        title: "Link revogado",
        description: "O link público desta carta deixou de abrir.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Não foi possível revogar",
        description: "O link continua como estava. Tente novamente.",
      });
    }
  };

  const handleDownload = async () => {
    if (!letter) return;
    
    try {
      const cleanContent = stripHtmlTags(letter.content);
      const deliveryDate = new Date(letter.delivery_date + "T00:00:00").toLocaleDateString("pt-BR");

      await generateLetterPDF(cleanContent, deliveryDate);

      toast({
        title: "Download iniciado",
        description: "Sua carta será baixada em formato PDF.",
      });
    } catch (error) {
      console.error("Erro no download:", error);
      toast({
        variant: "destructive",
        title: "Erro no download",
        description: "Não foi possível baixar a carta.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Carta não encontrada</h2>
        <Button onClick={() => navigate("/admin/cartas-disponiveis")}>
          Voltar às cartas
        </Button>
      </div>
    );
  }

  // Render HTML content properly
  const htmlContent = renderHtmlContent(letter.content || "");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/cartas-disponiveis")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Visualizar Carta</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Entregue em {format(new Date(letter.delivery_date + "T00:00:00"), "dd 'de' MMMM, yyyy", { locale: ptBR })}
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={handleShare} className="flex items-center gap-2 flex-1 sm:flex-none">
            <Share2 className="h-4 w-4" />
            <span className="sm:inline">Compartilhar</span>
          </Button>
          <Button variant="outline" onClick={handleRevokeShare} className="flex items-center gap-2 flex-1 sm:flex-none">
            <Link2Off className="h-4 w-4" />
            <span className="sm:inline">Revogar link</span>
          </Button>
          <Button onClick={handleDownload} className="flex items-center gap-2 flex-1 sm:flex-none">
            <Download className="h-4 w-4" />
            <span className="sm:inline">Download PDF</span>
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Letter Content */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-green-600" />
                Conteúdo da Carta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Letter Details */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600" />
                Detalhes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <Badge className="bg-green-100 text-green-800">
                    Disponível
                  </Badge>
                </div>
              </div>

              <Separator />

              <div>
                <label className="text-sm font-medium text-gray-500">Data de Entrega</label>
                <p className="mt-1 text-gray-900">
                  {format(new Date(letter.delivery_date + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Plano</label>
                <p className="mt-1 text-gray-900 capitalize">
                  {letter.plan_type || "Padrão"}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Criada em</label>
                <p className="mt-1 text-gray-900">
                  {format(new Date(letter.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-green-600" />
                Informações de Contato
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900 text-sm break-all">{letter.owner_email || letter.email}</span>
              </div>
              {letter.whatsapp && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-900 text-sm">{letter.whatsapp}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
